import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FormatAlignCenter,
  FormatAlignLeft,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  FormatUnderlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { EditorContent, Node, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { FontSize, TextStyle } from "@tiptap/extension-text-style";
import ConfirmationDialog from "./ConfirmationDialog";
import { structuredDocumentHtml } from "./structuredDocument";

export const MERGE_FIELDS = [
  ["first_name", "First name"],
  ["last_name", "Last name"],
  ["email", "Email address"],
  ["job_title", "Job title"],
  ["school_name", "School name"],
  ["school_phase", "School phase"],
  ["subject_name", "Primary subject"],
  ["subject_names", "All subjects"],
  ["education_level_name", "Primary education level"],
  ["education_level_names", "All education levels"],
];

const MOCK_VALUES = {
  first_name: "Alex",
  last_name: "Taylor",
  email: "alex.taylor@example.com",
  job_title: "Head of English",
  school_name: "Oakwood School",
  school_phase: "Secondary",
  subject_name: "English",
  subject_names: "English, History",
  education_level_name: "Secondary",
  education_level_names: "Secondary",
};

const EMPTY_DOCUMENT = {
  version: 1,
  type: "doc",
  content: [{ type: "paragraph" }],
};

const MergeField = Node.create({
  name: "mergeField",
  group: "inline",
  inline: true,
  atom: true,
  addAttributes() {
    return { key: { default: "first_name" } };
  },
  parseHTML() {
    return [{ tag: "span[data-merge-field]" }];
  },
  renderHTML({ node }) {
    return [
      "span",
      {
        "data-merge-field": node.attrs.key,
        style:
          "background:#e8f7f6;border-radius:4px;color:#286f6b;padding:1px 4px;",
      },
      `{{ ${node.attrs.key} }}`,
    ];
  },
});

const SenderSignature = Node.create({
  name: "senderSignature",
  group: "block",
  atom: true,
  parseHTML() {
    return [{ tag: "div[data-sender-signature]" }];
  },
  renderHTML() {
    return [
      "div",
      {
        "data-sender-signature": "true",
        style: "border-left:3px solid #52c2bd;padding:8px 12px;color:#286f6b;",
      },
      "Sender signature",
    ];
  },
});

const CallToAction = Node.create({
  name: "callToAction",
  group: "block",
  atom: true,
  addAttributes() {
    return {
      ctaId: { default: null },
      label: { default: "" },
      url: { default: "" },
      utmSource: { default: "fruitify" },
      utmMedium: { default: "email" },
      utmCampaign: { default: "" },
      utmContent: { default: "" },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-call-to-action]" }];
  },
  renderHTML({ node }) {
    return [
      "div",
      {
        "data-call-to-action": "true",
        "data-cta-id": node.attrs.ctaId,
        style:
          "background:#e8f7f6;border:1px solid #52c2bd;border-radius:4px;color:#286f6b;display:inline-block;padding:4px 8px;font-family:monospace;",
      },
      `{{${node.attrs.ctaId || "CTA_button"}}}`,
    ];
  },
});

const ctaNodes = (node, result = []) => {
  if (node?.type === "callToAction") result.push(node.attrs || {});
  (node?.content || []).forEach((child) => ctaNodes(child, result));
  return result;
};

const mapCtaNodes = (node, mapper) => {
  if (node?.type === "callToAction") return mapper(node);
  return node?.content
    ? {
        ...node,
        content: node.content.map((child) => mapCtaNodes(child, mapper)),
      }
    : node;
};

const migrateLegacyCta = (document, email) => {
  const value = editorDocument(document);
  const existing = ctaNodes(value);
  if (!email?.ctaUrl || existing.some((cta) => cta.ctaId)) return value;
  const attrs = {
    ctaId: "CTA_button_1",
    label: email.ctaLabel || "Continue",
    url: email.ctaUrl,
    utmSource: email.utmSource || "fruitify",
    utmMedium: email.utmMedium || "email",
    utmCampaign: email.utmCampaign || "",
    utmContent: email.utmContent || "",
  };
  let migrated = false;
  const result = mapCtaNodes(value, (node) => {
    if (migrated || node.attrs?.ctaId) return node;
    migrated = true;
    return { ...node, attrs };
  });
  return migrated
    ? result
    : {
        ...result,
        content: [...(result.content || []), { type: "callToAction", attrs }],
      };
};

const editorDocument = (document) => {
  let parsed = document;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      parsed = EMPTY_DOCUMENT;
    }
  }
  const value = parsed && Object.keys(parsed).length ? parsed : EMPTY_DOCUMENT;
  const doc = { ...value };
  delete doc.version;
  return doc;
};

const withVersion = (document) => ({ version: 1, ...document });

const containsSenderSignature = (node) =>
  node?.type === "senderSignature" ||
  (node?.content || []).some(containsSenderSignature);

const withoutSenderSignature = (node) => {
  const value = { ...node };
  if (value.content) {
    value.content = value.content
      .filter((child) => child.type !== "senderSignature")
      .map(withoutSenderSignature);
    if (value.type === "doc" && value.content.length === 0)
      value.content = [{ type: "paragraph" }];
  }
  return value;
};

const replaceMockFields = (value) =>
  value.replace(
    /<span[^>]*data-merge-field="([^"]+)"[^>]*>.*?<\/span>/g,
    (_, key) => MOCK_VALUES[key] || "",
  );

const subjectPreview = (subject) =>
  subject.replace(/{{\s*([a-z_]+)\s*}}/g, (_, key) => MOCK_VALUES[key] || "");

const escapeHtml = (value) =>
  String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

const marketingPreviewShell = (content) =>
  `<div style="margin:0;padding:24px 12px;background:#f5f5f5;font-family:Arial,sans-serif;color:#333333"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse:collapse"><tr><td align="center"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse:separate;max-width:600px;background:#ffffff;border:1px solid #e8e8e8;border-radius:8px"><tr><td align="center" style="padding:28px 30px 14px"><a href="https://fruitify.co.uk/" style="display:inline-block"><img src="${window.location.origin}/logo-horizontal-481.png" alt="Fruitify" width="220" style="display:block;max-width:220px;width:100%;height:auto"></a></td></tr><tr><td style="padding:12px 30px 30px;font-size:15px;line-height:1.6">${content}</td></tr><tr><td style="border-top:1px solid #eeeeee;padding:18px 30px 24px;text-align:center;font-size:12px;line-height:1.5;color:#777777"><span style="color:#527c79">You can unsubscribe from marketing emails here.</span><br>© Fruitify Labs Ltd. All rights reserved.</td></tr></table></td></tr></table></div>`;

const personalPreviewShell = (content) =>
  `<div style="margin:0;padding:12px 0;font-family:Arial,sans-serif;color:#333333;font-size:15px;line-height:1.6">${content}<p style="margin:28px 0 0;font-size:11px;line-height:1.5;color:#777777"><span style="color:#527c79">You can unsubscribe from marketing emails here.</span></p></div>`;

export default function EmailContentEditorDialog({
  open,
  email,
  onClose,
  onApply,
  sender,
  utmSuggestions = {},
  allowedFields,
}) {
  const mergeFields = allowedFields
    ? MERGE_FIELDS.filter(([key]) => allowedFields.includes(key))
    : MERGE_FIELDS;
  const [draft, setDraft] = useState(email || {});
  const [subjectSelection, setSubjectSelection] = useState([0, 0]);
  const [editorHtml, setEditorHtml] = useState("");
  const [, setDocumentRevision] = useState(0);
  const [discardOpen, setDiscardOpen] = useState(false);
  const subjectRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        horizontalRule: false,
        link: false,
        strike: false,
      }),
      Underline,
      TextStyle,
      FontSize,
      TextAlign.configure({
        types: ["heading", "paragraph"],
        alignments: ["left", "center"],
      }),
      MergeField,
      SenderSignature,
      CallToAction,
    ],
    content: migrateLegacyCta(email?.contentDocument, email),
    onUpdate: ({ editor: updatedEditor }) =>
      setEditorHtml(updatedEditor.getHTML()),
    onCreate: ({ editor: createdEditor }) =>
      setEditorHtml(createdEditor.getHTML()),
  });

  useEffect(() => {
    if (!open || !email) return;
    setDraft({ ...email });
    editor?.commands.setContent(migrateLegacyCta(email.contentDocument, email));
    if (editor) setEditorHtml(editor.getHTML());
  }, [open, email, editor]);

  const previewHtml = useMemo(() => {
    if (!editor) return "";
    let content = replaceMockFields(editorHtml || editor.getHTML());
    const signatureImage = sender?.signatureImageUrl
      ? `<img src="${escapeHtml(sender.signatureImageUrl)}" alt="" style="width:${sender.signatureImageWidth || 320}px;max-width:100%;height:auto">`
      : "";
    const linkedSignatureImage = sender?.signatureImageLinkUrl
      ? `<a href="${escapeHtml(sender.signatureImageLinkUrl)}">${signatureImage}</a>`
      : signatureImage;
    const signature = `${structuredDocumentHtml(sender?.signatureDocument)}${
      linkedSignatureImage ? `<p>${linkedSignatureImage}</p>` : ""
    }`;
    content = content.replace(
      /<div[^>]*data-sender-signature="true"[^>]*>.*?<\/div>/g,
      signature,
    );
    const ctas = ctaNodes(editor.getJSON());
    content = content.replace(
      /<div[^>]*data-call-to-action="true"[^>]*data-cta-id="([^"]+)"[^>]*>.*?<\/div>/g,
      (_, ctaId) => {
        const cta = ctas.find((item) => item.ctaId === ctaId) || {};
        return `<p style="margin:24px 0"><span style="background:#52c2bd;color:white;display:inline-block;padding:12px 20px;border-radius:6px;font-weight:600">${escapeHtml(cta.label || ctaId)}</span></p>`;
      },
    );
    return draft.useBrandedLayout === false
      ? personalPreviewShell(content)
      : marketingPreviewShell(content);
  }, [draft.useBrandedLayout, editor, editorHtml, sender]);

  const insertSubjectField = (key) => {
    const token = `{{ ${key} }}`;
    const [start, end] = subjectSelection;
    const subject = draft.subject || "";
    setDraft({
      ...draft,
      subject: subject.slice(0, start) + token + subject.slice(end),
    });
    requestAnimationFrame(() => {
      const position = start + token.length;
      subjectRef.current?.focus();
      subjectRef.current?.setSelectionRange(position, position);
    });
  };

  const signatureIncluded = containsSenderSignature(editor?.getJSON());
  const configuredCtas = ctaNodes(editor?.getJSON()).filter((cta) => cta.ctaId);
  const addCta = () => {
    const usedNumbers = configuredCtas.map((cta) =>
      Number(String(cta.ctaId).replace("CTA_button_", "")),
    );
    const nextNumber = Math.max(0, ...usedNumbers.filter(Number.isFinite)) + 1;
    editor
      .chain()
      .focus()
      .insertContent({
        type: "callToAction",
        attrs: {
          ctaId: `CTA_button_${nextNumber}`,
          label: "",
          url: "",
          utmSource: "fruitify",
          utmMedium: "email",
          utmCampaign: "",
          utmContent: "",
        },
      })
      .run();
    setEditorHtml(editor.getHTML());
    setDocumentRevision((value) => value + 1);
  };
  const updateCta = (ctaId, field, value) => {
    editor.commands.setContent(
      mapCtaNodes(editor.getJSON(), (node) =>
        node.attrs?.ctaId === ctaId
          ? { ...node, attrs: { ...node.attrs, [field]: value } }
          : node,
      ),
    );
    setEditorHtml(editor.getHTML());
    setDocumentRevision((revision) => revision + 1);
  };
  const removeCta = (ctaId) => {
    const remove = (node) => {
      if (!node?.content) return node;
      return {
        ...node,
        content: node.content
          .filter(
            (child) =>
              child.type !== "callToAction" || child.attrs?.ctaId !== ctaId,
          )
          .map(remove),
      };
    };
    editor.commands.setContent(remove(editor.getJSON()));
    setEditorHtml(editor.getHTML());
    setDocumentRevision((revision) => revision + 1);
  };
  const toggleSenderSignature = () => {
    if (signatureIncluded) {
      editor.commands.setContent(withoutSenderSignature(editor.getJSON()));
    } else {
      const document = withoutSenderSignature(editor.getJSON());
      editor.commands.setContent({
        ...document,
        content: [
          ...(document.content || [{ type: "paragraph" }]),
          { type: "senderSignature" },
        ],
      });
    }
    setEditorHtml(editor.getHTML());
  };

  const close = () => {
    const changed =
      JSON.stringify(withVersion(editor?.getJSON() || EMPTY_DOCUMENT)) !==
        JSON.stringify(email?.contentDocument || {}) ||
      draft.subject !== email?.subject ||
      draft.useBrandedLayout !== email?.useBrandedLayout;
    if (changed) {
      setDiscardOpen(true);
    } else {
      onClose();
    }
  };

  const apply = () => {
    onApply({
      subject: draft.subject || "",
      contentDocument: withVersion(editor.getJSON()),
      bodyHtml: "",
      bodyText: "",
      ctaLabel: "",
      ctaUrl: "",
      utmSource: "fruitify",
      utmMedium: "email",
      utmCampaign: "",
      utmContent: "",
      useBrandedLayout: draft.useBrandedLayout !== false,
    });
    onClose();
  };

  if (!email) return null;
  const toolbarButton = (label, active, action, icon) => (
    <Tooltip title={label}>
      <IconButton
        aria-label={label}
        color={active ? "primary" : "default"}
        sx={{ border: "none" }}
        onClick={action}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      <Dialog open={open} onClose={close} fullWidth maxWidth="xl">
        <DialogTitle>Edit email content</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack spacing={2}>
                <TextField
                  label="Subject"
                  value={draft.subject || ""}
                  inputRef={subjectRef}
                  onSelect={(event) =>
                    setSubjectSelection([
                      event.target.selectionStart,
                      event.target.selectionEnd,
                    ])
                  }
                  onChange={(event) =>
                    setDraft({ ...draft, subject: event.target.value })
                  }
                  required
                />
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Typography variant="caption">
                    Insert into subject:
                  </Typography>
                  <Select
                    size="small"
                    value=""
                    displayEmpty
                    sx={{ minWidth: 190 }}
                    onChange={(event) => insertSubjectField(event.target.value)}
                  >
                    <MenuItem value="" disabled>
                      Choose personalisation
                    </MenuItem>
                    {mergeFields.map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Email content
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ border: "1px solid", borderColor: "divider", p: 0.5 }}
                  >
                    {toolbarButton(
                      "Bold",
                      editor?.isActive("bold"),
                      () => editor.chain().focus().toggleBold().run(),
                      <FormatBold />,
                    )}
                    {toolbarButton(
                      "Italic",
                      editor?.isActive("italic"),
                      () => editor.chain().focus().toggleItalic().run(),
                      <FormatItalic />,
                    )}
                    {toolbarButton(
                      "Underline",
                      editor?.isActive("underline"),
                      () => editor.chain().focus().toggleUnderline().run(),
                      <FormatUnderlined />,
                    )}
                    {toolbarButton(
                      "Bulleted list",
                      editor?.isActive("bulletList"),
                      () => editor.chain().focus().toggleBulletList().run(),
                      <FormatListBulleted />,
                    )}
                    {toolbarButton(
                      "Numbered list",
                      editor?.isActive("orderedList"),
                      () => editor.chain().focus().toggleOrderedList().run(),
                      <FormatListNumbered />,
                    )}
                    {toolbarButton(
                      "Block quote",
                      editor?.isActive("blockquote"),
                      () => editor.chain().focus().toggleBlockquote().run(),
                      <FormatQuote />,
                    )}
                    {toolbarButton(
                      "Align left",
                      editor?.isActive({ textAlign: "left" }),
                      () => editor.chain().focus().setTextAlign("left").run(),
                      <FormatAlignLeft />,
                    )}
                    {toolbarButton(
                      "Align centre",
                      editor?.isActive({ textAlign: "center" }),
                      () => editor.chain().focus().setTextAlign("center").run(),
                      <FormatAlignCenter />,
                    )}
                    <Select
                      size="small"
                      value=""
                      displayEmpty
                      sx={{ minWidth: 120, ml: 1 }}
                      onChange={(event) =>
                        editor
                          .chain()
                          .focus()
                          .setFontSize(event.target.value)
                          .run()
                      }
                    >
                      <MenuItem value="" disabled>
                        Font size
                      </MenuItem>
                      <MenuItem value="12px">Small</MenuItem>
                      <MenuItem value="14px">Normal</MenuItem>
                      <MenuItem value="16px">Large</MenuItem>
                      <MenuItem value="20px">Heading</MenuItem>
                    </Select>
                    <Select
                      size="small"
                      value=""
                      displayEmpty
                      sx={{ minWidth: 185, ml: 1 }}
                      onChange={(event) =>
                        editor
                          .chain()
                          .focus()
                          .insertContent({
                            type: "mergeField",
                            attrs: { key: event.target.value },
                          })
                          .run()
                      }
                    >
                      <MenuItem value="" disabled>
                        Insert personalisation
                      </MenuItem>
                      {mergeFields.map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                      onClick={addCta}
                    >
                      Add CTA
                    </Button>
                    <Button
                      size="small"
                      variant={signatureIncluded ? "contained" : "outlined"}
                      onClick={toggleSenderSignature}
                    >
                      {signatureIncluded ? "Remove" : "Include"} sender
                      signature ({sender?.displayName || "choose a sender"})
                    </Button>
                  </Stack>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderTop: 0,
                      minHeight: 260,
                      p: 2,
                      "& .ProseMirror": { minHeight: 225, outline: "none" },
                      "& .ProseMirror p:first-of-type": { mt: 0 },
                    }}
                  >
                    <EditorContent editor={editor} />
                  </Box>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.useBrandedLayout !== false}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          useBrandedLayout: event.target.checked,
                        })
                      }
                    />
                  }
                  label="Use Fruitify branded layout"
                />
                {configuredCtas.map((cta) => (
                  <Box key={cta.ctaId} sx={{ pt: 1 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={2}
                    >
                      <Typography variant="subtitle2">{`{{${cta.ctaId}}}`}</Typography>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => removeCta(cta.ctaId)}
                      >
                        Remove CTA
                      </Button>
                    </Stack>
                    <Stack spacing={2}>
                      <TextField
                        label="CTA label"
                        value={cta.label || ""}
                        onChange={(event) =>
                          updateCta(cta.ctaId, "label", event.target.value)
                        }
                        required
                      />
                      <TextField
                        label="CTA URL"
                        type="url"
                        value={cta.url || ""}
                        onChange={(event) =>
                          updateCta(cta.ctaId, "url", event.target.value)
                        }
                        required
                      />
                    </Stack>
                    <Accordion
                      disableGutters
                      sx={{
                        boxShadow: "none",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>Campaign tracking</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={2}>
                          {[
                            ["utmSource", "UTM source", "sources"],
                            ["utmMedium", "UTM medium", "mediums"],
                            ["utmCampaign", "UTM campaign", "campaigns"],
                            ["utmContent", "UTM content", "contents"],
                          ].map(([field, label, suggestionKey]) => (
                            <Autocomplete
                              key={field}
                              freeSolo
                              options={utmSuggestions[suggestionKey] || []}
                              value={cta[field] || ""}
                              onChange={(_, value) =>
                                updateCta(cta.ctaId, field, value || "")
                              }
                              onInputChange={(_, value) =>
                                updateCta(cta.ctaId, field, value)
                              }
                              renderInput={(params) => (
                                <TextField {...params} label={label} />
                              )}
                            />
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Preview uses example details for Alex Taylor at Oakwood School.
              </Alert>
              <Box sx={{ bgcolor: "grey.100", p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" mb={1}>
                  {subjectPreview(draft.subject || "Untitled email")}
                </Typography>
                <Box
                  component="iframe"
                  title="Example email preview"
                  sandbox=""
                  srcDoc={previewHtml}
                  sx={{
                    width: "100%",
                    minHeight: 430,
                    border: 0,
                    bgcolor: "white",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="contained"
            onClick={apply}
            disabled={
              !draft.subject?.trim() ||
              !editor ||
              configuredCtas.some(
                (cta) => !cta.label?.trim() || !cta.url?.trim(),
              )
            }
          >
            Apply content
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={discardOpen}
        title="Discard email changes?"
        message="Your unapplied email content changes will be lost."
        confirmLabel="Discard"
        confirmColor="error"
        onCancel={() => setDiscardOpen(false)}
        onConfirm={() => {
          setDiscardOpen(false);
          onClose();
        }}
      />
    </>
  );
}
